// Pre-submit field validation. Catches malformed input before the
// pipeline runs — without these, a stray `à` in a timestamp ("19:5à")
// reaches yt-dlp / ffmpeg and the job fails minutes later with a
// cryptic stderr instead of an immediate, fixable form error.

/** Permissive YouTube watch URL match. Tolerates www., m., shorts/,
 * embed/, live/, and trailing query params (?t=, &si=, etc.). The
 * pipeline downstream re-parses with yt-dlp which is strict — we just
 * gate on "looks like a YouTube link" here. */
const YOUTUBE_RE =
  /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)[\w-]{6,}|youtu\.be\/[\w-]{6,})/i;

export function validateYouTubeUrl(input: string): string | null {
  const v = input.trim();
  if (!v) return "YouTube URL is required.";
  if (!/^https?:\/\//i.test(v)) return "URL must start with https://";
  if (!YOUTUBE_RE.test(v)) return "Doesn't look like a YouTube watch link.";
  return null;
}

/** Bare seconds ("90"), mm:ss ("1:30"), or h:mm:ss ("1:15:30"). */
const TIMESTAMP_RE = /^\d+(?::\d{1,2}){0,2}$/;

interface TimestampOpts {
  /** Treat empty input as an error. Defaults to false (empty = "skip"). */
  required?: boolean;
}

export function validateTimestamp(
  input: string,
  label: string,
  opts: TimestampOpts = {},
): string | null {
  const v = input.trim();
  if (!v) return opts.required ? `${label} is required.` : null;
  if (!TIMESTAMP_RE.test(v)) {
    return `${label} must be digits with colons — "90", "1:30", or "1:15:30".`;
  }
  // Component sanity. Minutes and seconds must be < 60 in
  // multi-segment forms; bare-seconds (single segment) can be any
  // size since it's an absolute offset.
  const parts = v.split(":").map(Number);
  if (parts.length === 3 && (parts[1]! >= 60 || parts[2]! >= 60)) {
    return `${label}: minutes and seconds must be < 60.`;
  }
  if (parts.length === 2 && parts[1]! >= 60) {
    return `${label}: seconds must be < 60.`;
  }
  return null;
}
